import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { DropdownTriangle, Folder, SyncDone, WarningTriangle, XCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'
import { formatLastActivityForCell } from '@/matter/utils/format-last-activity'
import { SyncManagedByPopover } from '@/user_files/external-storage/components/SyncManagedByPopover'

interface MatterFolderSyncRowProps {
  provider: ExternalStorageProvider
  canChooseFolder: boolean
  canDisconnectFolder: boolean
  isConnector: boolean
  connectorLabel: string | null
  disconnecting?: boolean
  lastSyncedAt: string | null
  syncedFolderName: string | null
  syncFailureMessage: string | null
  onChooseFolder: () => void
  onDisconnectFolder: () => void
}

export function MatterFolderSyncRow({
  provider,
  canChooseFolder,
  canDisconnectFolder,
  isConnector,
  connectorLabel,
  disconnecting = false,
  lastSyncedAt,
  syncedFolderName,
  syncFailureMessage,
  onChooseFolder,
  onDisconnectFolder,
}: MatterFolderSyncRowProps) {
  const providerLabel = kExternalStorageProviderDisplayName[provider]
  const isSynced = syncedFolderName != null
  const peerMapping = isSynced && !isConnector
  const hasFolderActions = !peerMapping || canDisconnectFolder
  let lastSyncLabel = syncFailureMessage ? 'Not yet synced' : 'Pending'
  if (lastSyncedAt) {
    const timestamp = Date.parse(lastSyncedAt)
    lastSyncLabel = Number.isNaN(timestamp) ? 'Unavailable' : formatLastActivityForCell(timestamp)
  }
  const statusLabel = syncFailureMessage ? 'Sync paused' : 'Synced'
  const statusIcon = syncFailureMessage ? (
    <WarningTriangle className="h-4 w-4 shrink-0 text-yellow-800" />
  ) : (
    <SyncDone className="h-4 w-4 shrink-0 text-green-800" />
  )
  const statusButton = (
    <Button
      type="button"
      variant="plain"
      color="secondary"
      size="sm"
      className="shrink-0 gap-1"
      disabled={disconnecting}
      leadingIcon={statusIcon}
      trailingIcon={<DropdownTriangle className="h-[18px] w-[18px] shrink-0 text-brand-neutral-600" />}
      aria-label={`Synced folder ${hasFolderActions ? 'options' : 'details'}: ${syncedFolderName}`}
    >
      {statusLabel}
    </Button>
  )

  return (
    <div
      className={clsx(
        'flex min-h-[76px] gap-4 border-t border-brand-neutral-200 px-4 pb-4 pt-3',
        isSynced ? 'items-center' : 'flex-col items-start sm:flex-row sm:items-center'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <Folder className="h-6 w-6 shrink-0 text-brand-neutral-400" />
          <p className="truncate text-base font-medium leading-6 tracking-[-0.32px] text-brand-neutral-950">{syncedFolderName ?? 'No folder synced yet'}</p>
        </div>
        {isSynced && <p className="mt-1 pl-[34px] text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">Last Sync: {lastSyncLabel}</p>}
        {syncFailureMessage && <p className="mt-1 text-sm leading-5 tracking-[-0.15px] text-yellow-800">{syncFailureMessage}</p>}
        {!isSynced && canChooseFolder && (
          <p className="mt-1 text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
            Choose which {providerLabel} folder to sync with this matter. Only one folder can be synced at a time.
          </p>
        )}
        {!canChooseFolder && !peerMapping && (
          <p className="mt-1 text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">Reconnect this account in Settings to change synced folders.</p>
        )}
      </div>
      {isSynced ? (
        peerMapping ? (
          canDisconnectFolder ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>{statusButton}</DropdownMenu.Trigger>
              <MenuContent align="end" collisionPadding={16} className="w-[297px] max-w-[calc(100vw-2rem)]">
                <MenuItemGroup className="pb-1">
                  <MenuItem label="Disconnect" icon={XCircle} onSelect={onDisconnectFolder} variant="destructive" disabled={disconnecting} />
                </MenuItemGroup>
                <p className="px-4 pb-4 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-600">
                  Sync managed by {connectorLabel ?? 'another matter member'}. Contact them to change the folder, or disconnect this sync.
                </p>
              </MenuContent>
            </DropdownMenu.Root>
          ) : (
            <SyncManagedByPopover connectorLabel={connectorLabel} action="disconnect">
              {statusButton}
            </SyncManagedByPopover>
          )
        ) : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>{statusButton}</DropdownMenu.Trigger>
            <MenuContent align="end" className="min-w-[150px]">
              <MenuItemGroup>
                <MenuItem label="Choose folder" icon={Folder} onSelect={onChooseFolder} variant="neutral" disabled={!canChooseFolder || disconnecting} />
                <MenuItem label="Disconnect" icon={XCircle} onSelect={onDisconnectFolder} variant="destructive" disabled={disconnecting} />
              </MenuItemGroup>
            </MenuContent>
          </DropdownMenu.Root>
        )
      ) : (
        <Button type="button" onClick={onChooseFolder} variant="outlined" color="primary" size="sm" className="shrink-0" disabled={!canChooseFolder}>
          Choose Folder
        </Button>
      )}
    </div>
  )
}
